import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEvent, getOrganizerBySlug, unregisterFromEvent, registerToEvent, confirmRegistration, getEventRegistrations } from '../services/api';
import WhitelabelLayout from '../layouts/WhitelabelLayout';
import PaymentModal from '../components/payment/PaymentModal';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';

/**
 * EventDetailWhitelabel - Página de detalle de un evento dentro del microsite del organizador
 * Renderiza el evento con el branding del organizador (whitelabel)
 */
const EventDetailWhitelabel = () => {
    const { slug, eventId } = useParams();
    const navigate = useNavigate();
    const userId = localStorage.getItem('userId');
    const queryClient = useQueryClient();

    // Obtener datos del organizador por slug
    const { data: organizer, isLoading: loadingOrganizer } = useQuery({
        queryKey: ['organizer', slug],
        queryFn: () => getOrganizerBySlug(slug),
        retry: 1
    });

    // Obtener datos del evento
    const { data: response, isLoading: loadingEvent, isError } = useQuery({
        queryKey: ['event', eventId],
        queryFn: () => getEvent(eventId),
        retry: 1
    });

    // Obtener inscripciones
    const { data: registrations = [] } = useQuery({
        queryKey: ['registrations', eventId],
        queryFn: () => getEventRegistrations(eventId),
        enabled: !!eventId
    });

    const unregisterMutation = useMutation({
        mutationFn: () => unregisterFromEvent(eventId),
        onSuccess: () => {
            toast.success('Te has dado de baja del pozo');
            queryClient.invalidateQueries(['event', eventId]);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Error al cancelar la inscripción');
        }
    });

    const [isPaymentModalOpen, setIsPaymentModalOpen] = React.useState(false);
    const [partnerName, setPartnerName] = React.useState('');

    const confirmRegistrationMutation = useMutation({
        mutationFn: (paymentData) => confirmRegistration(eventId, paymentData),
        onSuccess: () => {
            toast.success('¡Registro confirmado y pagado!');
            setIsPaymentModalOpen(false);
            queryClient.invalidateQueries(['event', eventId]);
        },
        onError: (error) => {
            toast.error('Error al confirmar el registro en el servidor');
            console.error(error);
        }
    });

    // Correctly placed registerMutation before any conditional returns (Hooks Rule)
    const registerMutation = useMutation({
        mutationFn: () => {
             // Safe access to event properties via response
            const price = parseFloat(response?.pricePerPlayer || 0);
            return registerToEvent(eventId, {
                status: price > 0 ? 'pending_payment' : 'confirmed',
                player1Id: userId,
                priceSnapshot: price,
                partnerName: partnerName.trim() // Pass partner name
            });
        },
        onSuccess: (data) => {
            const price = parseFloat(response?.pricePerPlayer || 0);
            if (price > 0 && data?.registration?.status === 'pending_payment') {
                 // Modal already open via handleRegister
            } else {
                toast.success('¡Inscripción realizada con éxito!');
                queryClient.invalidateQueries(['event', eventId]);
                queryClient.invalidateQueries(['registrations', eventId]);
                setPartnerName(''); // Clear field
            }
        },
        onError: (error) => {
            setIsPaymentModalOpen(false); // Rollback
            toast.error(error.response?.data?.message || 'Error al inscribirse');
        }
    });

    const isLoading = loadingOrganizer || loadingEvent;

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <LoadingSpinner />
            </div>
        );
    }

    const event = response;

    if (isError || !event || !organizer) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center p-8 bg-white rounded-3xl shadow-xl max-w-md mx-4 border border-slate-100">
                    <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                        ⚠️
                    </div>
                    <h1 className="text-2xl font-bold text-slate-800 mb-2">Evento no encontrado</h1>
                    <p className="text-slate-500 mb-8">Lo sentimos, no hemos podido encontrar el evento que buscas.</p>
                    <button
                        onClick={() => navigate(`/o/${slug}`)}
                        className="inline-block bg-slate-800 text-white px-8 py-3 rounded-xl font-bold hover:bg-slate-700 transition-colors"
                    >
                        Volver al inicio
                    </button>
                </div>
            </div>
        );
    }

    const { title, club, date, startTime, pricePerPlayer, levelMin, levelMax, description, posterUrl, cancellationDeadlineHours } = event;
    const brandingColor = organizer.brandColor || '#72c02c';

    // Check if user is registered using fetched registrations
    const isRegistered = registrations.some(r => 
        (r.player1Id === userId || r.player2Id === userId) && 
        r.status === 'confirmed'
    );

    const handleUnregister = () => {
        if (window.confirm('¿Estás seguro de que quieres darte de baja?')) {
            unregisterMutation.mutate();
        }
    };

    const handleRegister = () => {
        const price = parseFloat(pricePerPlayer || 0);
        if (price > 0) {
            setIsPaymentModalOpen(true); // Optimistic UI
        }
        registerMutation.mutate();
    };

    return (
        <WhitelabelLayout organizer={organizer}>
            <div className="min-h-screen pb-12">
                {/* Header Image */}
                <div className="h-64 relative overflow-hidden" style={{ backgroundColor: brandingColor }}>
                    <img
                        src={posterUrl || club?.photoUrl || '/default-club.jpg'}
                        alt={club?.name}
                        className="w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 text-white max-w-7xl mx-auto">
                        <h1 className="text-2xl md:text-4xl font-bold mb-2 break-words">{title}</h1>
                        <p className="text-xl opacity-90">{club?.name}</p>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 -mt-8 relative z-10">
                    <div className="bg-white rounded-[32px] shadow-xl p-6 md:p-8 border border-slate-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">

                            {/* LEFT COLUMN: Poster & Info */}
                            <div className="space-y-8">
                                {/* Poster if available */}
                                {posterUrl && (
                                    <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100 group">
                                        <img
                                            src={posterUrl}
                                            alt={`Cartel ${title}`}
                                            className="w-full h-auto object-contain bg-gray-50 transition-transform duration-700 group-hover:scale-105"
                                        />
                                    </div>
                                )}

                                {/* ATTRACTIVE INFO GRID */}
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                                        <span className="p-2 rounded-xl text-2xl" style={{ backgroundColor: `${brandingColor}20` }}>📋</span>
                                        <span>Detalles del Evento</span>
                                    </h2>

                                    <div className="grid grid-cols-2 gap-4">
                                        {/* Date */}
                                        <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 hover:shadow-md transition-all group">
                                            <div className="text-2xl mb-2 group-hover:scale-110 transition-transform origin-left">📅</div>
                                            <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Fecha</div>
                                            <div className="font-bold text-slate-900 capitalize text-lg leading-tight">
                                                {format(new Date(date), "EEE d MMM", { locale: es })}
                                            </div>
                                        </div>

                                        {/* Time */}
                                        <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 hover:shadow-md transition-all group">
                                            <div className="text-2xl mb-2 group-hover:scale-110 transition-transform origin-left">⏰</div>
                                            <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Horario</div>
                                            <div className="font-bold text-slate-900 text-lg leading-tight">
                                                {startTime}
                                            </div>
                                        </div>

                                        {/* Level */}
                                        <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 hover:shadow-md transition-all group">
                                            <div className="text-2xl mb-2 group-hover:scale-110 transition-transform origin-left">⚖️</div>
                                            <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Nivel</div>
                                            <div className="font-bold text-slate-900 text-lg leading-tight">
                                                {levelMin} - {levelMax}
                                            </div>
                                        </div>

                                        {/* Price */}
                                        <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 hover:shadow-md transition-all group">
                                            <div className="text-2xl mb-2 group-hover:scale-110 transition-transform origin-left">💰</div>
                                            <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Precio</div>
                                            <div className="font-bold text-lg leading-tight" style={{ color: brandingColor }}>
                                                {pricePerPlayer}€
                                            </div>
                                        </div>
                                    </div>

                                    {/* Location Full Width */}
                                    <div className="mt-4 bg-slate-50 p-5 rounded-2xl border border-slate-100 hover:shadow-md transition-all group">
                                        <div className="flex items-start gap-4">
                                            <div className="text-3xl group-hover:scale-110 transition-transform mt-1">📍</div>
                                            <div className="flex-1">
                                                <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1">Ubicación</div>
                                                <div className="font-bold text-slate-900 text-lg">{club?.name}</div>
                                                <div className="text-slate-600 leading-tight">{club?.address}, {club?.city}</div>
                                                <a
                                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(club?.name + ' ' + club?.city)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs font-bold hover:underline mt-2 inline-flex items-center gap-1"
                                                    style={{ color: brandingColor }}
                                                >
                                                    Ver en el mapa
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column: CTA & Description */}
                            <div className="space-y-8">
                                <div className="bg-slate-50 rounded-[32px] p-8 border border-slate-100 flex flex-col justify-center items-center text-center sticky top-24 shadow-sm">
                                    {isRegistered ? (
                                        <>
                                            <h3 className="text-2xl font-bold mb-2" style={{ color: brandingColor }}>¡Estás inscrito!</h3>
                                            <p className="text-slate-500 mb-8">Tienes tu plaza asegurada.</p>
                                            <button
                                                onClick={handleUnregister}
                                                disabled={unregisterMutation.isPending}
                                                className="w-full bg-red-100 text-red-600 font-black py-4 px-6 rounded-xl transition-all transform hover:-translate-y-1 shadow-sm hover:bg-red-200 text-lg uppercase tracking-wide border border-red-200"
                                            >
                                                {unregisterMutation.isPending ? 'Cancelando...' : 'Cancelar Inscripción'}
                                            </button>
                                            <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                                                Puedes cancelar gratuitamente hasta {cancellationDeadlineHours || 24} horas antes del evento.
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <h3 className="text-2xl font-bold mb-2 text-slate-900">¡Inscríbete ahora!</h3>
                                            <p className="text-slate-500 mb-8">Quedan pocas plazas disponibles para este evento.</p>
                                            
                                            {/* Partner Name Input */}
                                            <div className="w-full mb-4 text-left">
                                                <label htmlFor="partnerName" className="block text-sm font-bold text-slate-700 mb-1">
                                                    ¿Juegas con pareja?
                                                </label>
                                                <input
                                                    type="text"
                                                    id="partnerName"
                                                    placeholder="Nombre de tu pareja (Opcional)"
                                                    value={partnerName}
                                                    onChange={(e) => setPartnerName(e.target.value)}
                                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-slate-500 focus:ring-2 focus:ring-slate-200 outline-none transition-all"
                                                />
                                            </div>

                                            <button
                                                onClick={handleRegister}
                                                disabled={registerMutation.isPending}
                                                className="w-full text-white font-black py-4 px-6 rounded-xl transition-all transform hover:-translate-y-1 shadow-lg text-lg uppercase tracking-wide border-2 border-white/10"
                                                style={{ backgroundColor: brandingColor }}
                                            >
                                                {registerMutation.isPending ? 'Procesando...' : `Pagar ${pricePerPlayer}€ y Unirse`}
                                            </button>
                                            <div className="flex items-center gap-2 mt-6 text-xs text-slate-400">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                                Pago 100% seguro garantizado
                                            </div>
                                        </>
                                    )}
                                </div>

                                {description && (
                                    <div className="pt-8 border-t border-slate-100">
                                        <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                            <span>📝</span> Acerca del evento
                                        </h3>
                                        <div className="bg-white p-6 rounded-2xl border border-slate-100 text-slate-600 leading-relaxed shadow-sm">
                                            {description}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <PaymentModal
                    isOpen={isPaymentModalOpen}
                    onClose={() => setIsPaymentModalOpen(false)}
                    event={event}
                    onSuccess={(paymentIntent) => {
                        confirmRegistrationMutation.mutate({ paymentIntentId: paymentIntent?.id });
                    }}
                />
            </div>
        </WhitelabelLayout>
    );
};

export default EventDetailWhitelabel;
